/**
 * Cliente do serviço Agentes (services/agentes, 127.0.0.1:8106). Same-origin: sessão S2 via
 * nginx auth_request; escritas levam X-Token injetado só no servidor.
 */
export class AgentesApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'AgentesApiError'
    this.status = status
  }
}

const BASE = '/api/agentes'

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  let resp: Response
  try {
    resp = await fetch(BASE + path, { credentials: 'include', cache: 'no-store', ...init })
  } catch {
    throw new AgentesApiError(0, 'Serviço de Agentes inacessível (fiesta-agentes, porta 8106).')
  }
  if (!resp.ok) {
    const corpo = (await resp.json().catch(() => null)) as { detail?: unknown; erro?: string } | null
    const detail = typeof corpo?.detail === 'string' ? corpo.detail : undefined
    throw new AgentesApiError(resp.status, corpo?.erro || detail || `Serviço de Agentes respondeu HTTP ${resp.status}.`)
  }
  return resp.json() as Promise<T>
}

const json = (body: unknown): RequestInit => ({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })

export type Semaforo = 'verde' | 'amarelo' | 'vermelho' | 'pausado' | 'rodando'

export type Agente = {
  id: 'ativador_promocoes' | 'monitor_margens' | 'coleta_score' | 'coleta_promocoes' | 'promocoes_shopee' | 'ofertas_shopee'
  nome: string
  objetivo: string
  agenda: string
  tela: string
  ultima_rodada: string | null
  ok: boolean | null
  numeros: Record<string, number | string | null>
  rotulos: Record<string, string>
  avisos: string[]
  semaforo: Semaforo
  proxima: string | null
  parametros: Record<string, unknown>
  pausado: boolean
  rodando_desde?: string
  // detalhe
  historico?: Array<Record<string, unknown>>
  log?: string[]
  relatorio_hoje?: string | null
  /** Promoções da Shopee: avisos da última rodada, com o link do anúncio quando o aviso é de um produto. */
  alertas?: AlertaAgente[]
  excecoes?: { total: number; vencendo: number; vencidas: number }
  rodar_agora?: { ativo: boolean; inicio?: string; fim?: string; cmd?: string } | null
  log_rodar_agora?: string[]
}

export type AlertaAgente = { loja: string; gravidade: 'critico' | 'atencao' | 'info'; codigo: string; mensagem: string; link?: string | null; sku?: string | null }

export type EmailInfo = { configurado: boolean; host: string | null; de: string | null; para: string | null; porta: number }

export type ItemMonitor = {
  mlb: string
  conta: string
  apelido: string
  titulo: string | null
  sku: string | null
  familia: string
  estoque: number | null
  curva: string | null
  pedidos_30d: number | null
  faturamento_30d: number | null
  preco_hoje: number | null
  mc_hoje_pct: number
  cmv: number | null
  cmv_origem: string | null
  frete_hoje: number | null
  comissao_pct: number | null
  imposto_pct: number | null
  imposto_origem: string | null
  logistica: string | null
  thumbnail: string | null
  n_promo_ativa: number | null
  mc_negativa: boolean
  impossivel?: boolean
  motivo?: string
  preco_alvo?: number
  frete_no_alvo?: number
  frete_origem?: string
  mc_nova_pct?: number
  aumento_pct?: number
  regime_frete?: string
  esforco?: 'verde' | 'amarelo' | 'vermelho' | 'total'
  decisao?: { decisao: 'aprovado' | 'reprovado'; motivo: string; quando: string; por: string | null; preco_alvo: number | null; alterou_alvo: boolean }
}

export type FilaMonitor = {
  ok: boolean
  gerado_em: string | null
  piso_pct: number | null
  itens: ItemMonitor[]
  familias: Record<string, { n: number; mc_media: number; aumento_medio: number }>
  motivo?: string
}

export const fetchAgentes = () => api<{ ok: boolean; agentes: Agente[]; email: EmailInfo; escrita_liberada: boolean }>('')
export const fetchAgente = (id: string) => api<{ ok: boolean; agente: Agente }>(`/${id}`)
export const pausar = (id: string) => api<{ ok: boolean; pausado: boolean }>(`/${id}/pausar`, { method: 'POST' })
export const retomar = (id: string) => api<{ ok: boolean; pausado: boolean }>(`/${id}/retomar`, { method: 'POST' })
export const rodarAgora = (id: string) => api<{ ok: boolean; mensagem?: string }>(`/${id}/rodar`, { method: 'POST' })
export const fetchFilaMonitor = () => api<FilaMonitor>('/monitor-margens/fila')
export const decidirMonitor = (body: { mlb: string; decisao: 'aprovado' | 'reprovado'; motivo?: string; preco_alvo?: number | null }) =>
  api<{ ok: boolean; item: ItemMonitor }>('/monitor-margens/decisao', json(body))
export const urlAprovadosCsv = () => `${BASE}/monitor-margens/aprovados.csv`
export const fetchEmailStatus = () => api<{ ok: boolean; previa: string } & EmailInfo>('/email/status')
export const enviarEmailAgora = () => api<{ ok: boolean; para?: string }>('/email/enviar-agora', { method: 'POST' })
